import axios from 'axios';

import type { FieldType } from './fields.api';

const API_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

const client = axios.create({ baseURL: API_URL, timeout: 15_000 });

type Envelope<T> = { data: T };

export type SignField = {
  id: string;
  documentId: string;
  type: FieldType;
  pageNumber: number;
  posX: number;
  posY: number;
  width: number;
  height: number;
  isRequired: boolean;
  defaultValue?: string | null;
};

export type SignChainEntry = {
  id: string;
  name: string;
  email: string;
  role: 'signer' | 'approver' | 'cc' | 'witness' | 'for_info';
  status: 'pending' | 'sent' | 'viewed' | 'signed' | 'declined' | 'expired';
  signingOrder: number;
  signedAt: string | null;
  declinedAt: string | null;
  isMe: boolean;
};

export type SignContext = {
  envelope: {
    id: string;
    title: string;
    message: string | null;
    status: string;
    workflowType: 'sequential' | 'parallel' | 'hybrid';
    signatureOptional: boolean;
    expiresAt: string | null;
    previousRevision: { documentNumber: string | null; revision: number } | null;
  };
  chain: SignChainEntry[];
  sender: {
    firstName: string;
    lastName: string;
    email: string;
    organizationName: string;
  };
  recipient: {
    id: string;
    email: string;
    name: string;
    role: string;
    status: string;
    authMethod: string;
    savedSignatureUrl: string | null;
    savedInitialUrl: string | null;
  };
  documents: Array<{ id: string; originalName: string; pagesCount: number }>;
  fields: SignField[];
  reviewComments: ReviewComment[];
};

export type ReviewComment = {
  id: string;
  page: number;
  xPct?: number;
  yPct?: number;
  documentId?: string;
  text: string;
  author: string;
  role: string;
  at: string;
};

export const signApi = {
  async load(token: string): Promise<SignContext> {
    const { data } = await client.get<Envelope<SignContext>>(`/sign/${token}`);
    return data.data;
  },

  /** Read-only session from the link alone — lets the recipient open the PDF
   *  and leave notes before the emailed code is requested. */
  async startReviewSession(token: string): Promise<{ sessionToken: string; expiresIn: number }> {
    const { data } = await client.post<Envelope<{ sessionToken: string; expiresIn: number }>>(
      `/sign/${token}/review-session`,
    );
    return data.data;
  },

  async requestOtp(token: string): Promise<{ delivered: boolean }> {
    const { data } = await client.post<Envelope<{ delivered: boolean }>>(
      `/sign/${token}/auth/request`,
    );
    return data.data;
  },

  async verifyOtp(token: string, otp: string): Promise<{ sessionToken: string; expiresIn: number }> {
    const { data } = await client.post<Envelope<{ sessionToken: string; expiresIn: number }>>(
      `/sign/${token}/auth/verify`,
      { otp },
    );
    return data.data;
  },

  async complete(
    token: string,
    sessionToken: string,
    fields: Array<{ id: string; value: string }>,
    note?: string,
  ): Promise<{ envelopeStatus: string }> {
    const { data } = await client.post<Envelope<{ envelopeStatus: string }>>(
      `/sign/${token}/complete`,
      { sessionToken, fields, ...(note ? { note } : {}) },
    );
    return data.data;
  },

  async decline(token: string, sessionToken: string, reason: string): Promise<void> {
    await client.post(`/sign/${token}/decline`, { sessionToken, reason });
  },

  /** Add a note anchored to a page inside the document (reviewer/approver). */
  async addComment(
    token: string,
    sessionToken: string,
    input: { page: number; text: string; xPct?: number; yPct?: number; documentId?: string },
  ): Promise<{ comments: ReviewComment[] }> {
    const { data } = await client.post<Envelope<{ comments: ReviewComment[] }>>(
      `/sign/${token}/comments`,
      { sessionToken, ...input },
    );
    return data.data;
  },

  async requestRevision(
    token: string,
    sessionToken: string,
    reason: string,
  ): Promise<{ newEnvelopeUuid: string }> {
    const { data } = await client.post<Envelope<{ newEnvelopeUuid: string }>>(
      `/sign/${token}/request-revision`,
      { sessionToken, reason },
    );
    return data.data;
  },

  /** Returns the full URL the iframe/Document loader can hit. */
  documentUrl(token: string, documentId: string): string {
    return `${API_URL}/sign/${token}/document/${documentId}/file`;
  },

  /** Fetch the previous (superseded, "NOT ACTUAL") version as a blob URL so an
   *  approver can open it in a new tab while reviewing a revision. */
  async previousDocumentBlobUrl(token: string, sessionToken: string): Promise<string> {
    const res = await client.get(`/sign/${token}/previous-document/file`, {
      responseType: 'blob',
      headers: { 'x-sign-session': sessionToken },
    });
    return URL.createObjectURL(res.data as Blob);
  },
};
